export interface TokenPair {
  readonly accessToken: string
  readonly refreshToken: string
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details?: Record<string, string>

  constructor(status: number, code: string, message: string, details?: Record<string, string>) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    if (details !== undefined) this.details = details
  }

  /** True when the API rejected the shape of the request, so a form can show field errors. */
  get isValidation(): boolean {
    return this.code === 'validation_failed'
  }
}

export interface ApiClientOptions {
  readonly baseUrl: string
  readonly fetchImpl?: typeof fetch
  readonly readTokens: () => TokenPair | null
  readonly writeTokens: (tokens: TokenPair) => void
  readonly onSignedOut: () => void
}

export interface ApiClient {
  get<T>(path: string): Promise<T>
  getBlob(path: string): Promise<Blob>
  post<T>(path: string, body?: unknown): Promise<T>
  patch<T>(path: string, body?: unknown): Promise<T>
  del<T>(path: string): Promise<T>
  setTokens(tokens: TokenPair): void
  clearTokens(): void
}

const NO_BODY_STATUSES = new Set([204, 205, 304])

/**
 * The only module that knows a token exists. Screens call get/post and never touch
 * storage, which is what makes the refresh dance testable and stops six components
 * inventing their own header handling.
 */
export function createApiClient(options: ApiClientOptions): ApiClient {
  const doFetch = options.fetchImpl ?? fetch
  // One shared refresh promise: three components mounting at once produce three 401s,
  // and three parallel refreshes would rotate the token out from under each other.
  let refreshing: Promise<boolean> | null = null
  let overrideTokens: TokenPair | null = null

  function readTokens(): TokenPair | null {
    return overrideTokens ?? options.readTokens()
  }

  function storeTokens(tokens: TokenPair): void {
    overrideTokens = tokens
    options.writeTokens(tokens)
  }

  function clearTokens(): void {
    overrideTokens = null
    options.onSignedOut()
  }

  async function parseError(response: Response): Promise<ApiError> {
    let code = 'unknown'
    let message = `Request failed with status ${response.status}`
    let details: Record<string, string> | undefined
    try {
      const body = (await response.json()) as {
        error?: { code?: string; message?: string; details?: Record<string, string> }
      }
      if (body.error?.code !== undefined) code = body.error.code
      if (body.error?.message !== undefined) message = body.error.message
      if (body.error?.details !== undefined) details = body.error.details
    } catch {
      // A 502 from nginx is HTML, not JSON. Keep the status-derived message.
    }
    return new ApiError(response.status, code, message, details)
  }

  async function refresh(): Promise<boolean> {
    const current = readTokens()
    if (current === null) return false

    const response = await doFetch(`${options.baseUrl}/api/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken: current.refreshToken }),
    })
    if (!response.ok) {
      clearTokens()
      return false
    }
    const next = (await response.json()) as TokenPair
    storeTokens(next)
    return true
  }

  async function request(method: string, path: string, body?: unknown, isRetry = false): Promise<Response> {
    const tokens = readTokens()
    const headers: Record<string, string> = {}
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    if (tokens !== null) headers['Authorization'] = `Bearer ${tokens.accessToken}`

    const response = await doFetch(`${options.baseUrl}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    })

    // 401 only. A 403 is a permission decision and refreshing changes nothing.
    if (response.status === 401 && !isRetry) {
      refreshing ??= refresh().finally(() => {
        refreshing = null
      })
      const refreshed = await refreshing
      if (refreshed) return request(method, path, body, true)
      throw await parseError(response)
    }

    return response
  }

  async function readJson<T>(method: string, path: string, body?: unknown): Promise<T> {
    const response = await request(method, path, body)
    if (!response.ok) throw await parseError(response)
    if (NO_BODY_STATUSES.has(response.status)) return undefined as T
    const text = await response.text()
    return (text === '' ? undefined : JSON.parse(text)) as T
  }

  async function readBlob(path: string): Promise<Blob> {
    const response = await request('GET', path)
    if (!response.ok) throw await parseError(response)
    return response.blob()
  }

  return {
    get: <T,>(path: string) => readJson<T>('GET', path),
    getBlob: (path: string) => readBlob(path),
    post: <T,>(path: string, body?: unknown) => readJson<T>('POST', path, body),
    patch: <T,>(path: string, body?: unknown) => readJson<T>('PATCH', path, body),
    del: <T,>(path: string) => readJson<T>('DELETE', path),
    setTokens: (tokens: TokenPair) => {
      storeTokens(tokens)
    },
    clearTokens,
  }
}
